'use client';

import { ColumnsType } from 'antd/es/table';
import {
  InputMaybe,
  Invoice,
  InvoicePage,
  InvoiceSortAttributeEnum,
  SortOrderEnum
} from 'gql/graphql';
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Control } from 'react-hook-form';
import useSearchParamsInvoices, { FiltersInvoicesForm } from './useSearchParamsInvoices';
import useTableInvoices from './useTableInvoices';

type TInvoicesContext = {
  invoices?: InvoicePage;
  isLoading: boolean;
  isPending: boolean;
  isRetrieving: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  handleClear: () => void;
  control: Control<FiltersInvoicesForm>;
  filtersAdded: boolean;
  columns: ColumnsType<Invoice>;
  sortAttribute?: InputMaybe<InvoiceSortAttributeEnum>;
  sortOrder?: InputMaybe<SortOrderEnum>;
  currentInvoiceId?: string | null;
  setCurrentInvoiceId: Dispatch<SetStateAction<string | null>>;
  currentInvoice?: Invoice | null;
  handleSelectInvoice: (id: string) => () => void;
};

const initialState = {
  isLoading: false,
  isPending: false,
  isRetrieving: false,
  page: 1,
  setPage: () => {},
  totalPages: 0,
  handleClear: () => {},
  control: {} as Control<FiltersInvoicesForm>,
  filtersAdded: false,
  columns: [],
  sortAttribute: undefined,
  sortOrder: undefined,
  currentInvoiceId: null,
  setCurrentInvoiceId: () => {},
  currentInvoice: null,
  handleSelectInvoice: () => () => {}
};

const InvoicesContext = createContext<TInvoicesContext>(initialState);

const useInvoicesContext = (): TInvoicesContext => useContext(InvoicesContext);

const InvoicesProvider: FC<PropsWithChildren<unknown>> = ({ children }): JSX.Element => {
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | null>(null);

  const handleSelectInvoice = useCallback(
    (id: string) => () => {
      setCurrentInvoiceId(id);
    },
    []
  );

  const {
    isLoading,
    isPending,
    page,
    setPage,
    totalPages,
    invoices,
    isRetrieving,
    control,
    filtersAdded,
    handleClear,
    sortAttribute,
    sortOrder,
    refetch
  } = useSearchParamsInvoices({ currentInvoiceId });

  const currentInvoice = useMemo(() => {
    if (!currentInvoiceId || isLoading || !invoices?.nodesCount) return null;

    return (invoices?.nodes && invoices?.nodes?.find((i) => i?.id == currentInvoiceId)) ?? null;
  }, [currentInvoiceId, isLoading, invoices?.nodesCount, invoices?.nodes]);

  const { columns } = useTableInvoices({ refetch, handleSelectInvoice });

  const currentIdDetails = useMemo(() => {
    const pathSegments = window.location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (/^\d+$/.test(lastSegment)) return lastSegment;

    return null;
  }, []);

  const loadCurrentId = useCallback(() => {
    if (currentIdDetails) return setCurrentInvoiceId(currentIdDetails);
    if (!invoices?.nodesCount) return;

    return setCurrentInvoiceId(invoices?.nodes?.at(0)?.id as string);
  }, [currentIdDetails, invoices]);

  useEffect(() => {
    loadCurrentId();
  }, [loadCurrentId]);

  return (
    <InvoicesContext.Provider
      value={{
        invoices,
        isLoading,
        isPending,
        isRetrieving,
        page,
        setPage,
        totalPages,
        handleClear,
        currentInvoice,
        currentInvoiceId,
        setCurrentInvoiceId,
        control,
        filtersAdded,
        columns,
        sortAttribute,
        sortOrder,
        handleSelectInvoice
      }}
    >
      {children}
    </InvoicesContext.Provider>
  );
};

export { InvoicesProvider, useInvoicesContext };
